// Canvas de passos e ligações — Fase 10.
//
// Escrito à mão, sem biblioteca de grafo. A solicitação previa `reactflow` e a
// decisão foi não trazer: o grafo deste módulo é pequeno e FECHADO — nove tipos
// de passo, saída única exceto na condição, sem agrupamento e sem nó
// redimensionável. O que a lib resolve de difícil (handles arbitrários, layout
// automático, subfluxos) não acontece aqui, e o que sobra é o que está neste
// arquivo: posicionar caixa, arrastar e desenhar curva.
//
// DESENHO EM DUAS CAMADAS: as ligações são um <svg> absoluto no fundo, os
// passos são <div> por cima. Fazer os passos em SVG também obrigaria a
// reimplementar quebra de linha, foco e acessibilidade de botão — coisas que o
// HTML já faz.
//
// O canvas rola DENTRO do próprio contêiner (`overflow-auto`), nunca empurra a
// página. É o critério de aceite "funciona em tela de notebook sem rolagem
// horizontal na página".

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flag, Trash2 } from 'lucide-react';
import { passo } from '../catalogoNos';
import type { ArestaFluxo, Grafo, NoFluxo } from '../tipos';
import type { Problema } from '../validarFluxo';

export const LARGURA_NO = 208;
export const ALTURA_NO = 76;
const LARGURA_TELA = 2600;
const ALTURA_TELA = 1800;

/** Ligação sendo criada: de qual passo e por qual ramo. */
export interface Ligando {
  de: string;
  quando: 'verdadeiro' | 'falso' | null;
}

interface Props {
  grafo: Grafo;
  selecionado: string | null;
  problemas: Problema[];
  ligando: Ligando | null;
  /** Passos por onde um run passou — o caminho percorrido da simulação. */
  destacados?: string[];
  somenteLeitura?: boolean;
  onSelecionar: (noKey: string | null) => void;
  onMover: (noKey: string, posicao: { x: number; y: number }) => void;
  onIniciarLigacao: (ligando: Ligando | null) => void;
  onLigar: (para: string) => void;
  onRemoverAresta: (arestaId: string) => void;
  onDefinirInicial: (noKey: string) => void;
}

function centroSaida(no: NoFluxo) {
  const p = no.posicao ?? { x: 0, y: 0 };
  return { x: p.x + LARGURA_NO / 2, y: p.y + ALTURA_NO };
}

function centroEntrada(no: NoFluxo) {
  const p = no.posicao ?? { x: 0, y: 0 };
  return { x: p.x + LARGURA_NO / 2, y: p.y };
}

/**
 * Curva de Bézier vertical entre dois passos.
 *
 * A força da curva cresce com a distância para o desenho não virar um "S"
 * apertado quando os passos estão longe, nem um arco exagerado quando estão
 * colados.
 */
function caminho(de: { x: number; y: number }, para: { x: number; y: number }): string {
  const forca = Math.max(40, Math.min(160, Math.abs(para.y - de.y) / 2));
  return `M ${de.x} ${de.y} C ${de.x} ${de.y + forca}, ${para.x} ${para.y - forca}, ${para.x} ${para.y}`;
}

export default function Canvas({
  grafo, selecionado, problemas, ligando, destacados = [], somenteLeitura = false,
  onSelecionar, onMover, onIniciarLigacao, onLigar, onRemoverAresta, onDefinirInicial,
}: Props) {
  const areaRef = useRef<HTMLDivElement>(null);
  const [arrastando, setArrastando] = useState<{ noKey: string; dx: number; dy: number } | null>(null);
  const [arestaSobre, setArestaSobre] = useState<string | null>(null);

  const porChave = new Map(grafo.nos.map((n) => [n.node_key, n]));
  const comErro = new Set(problemas.filter((p) => p.nivel === 'erro' && p.noKey).map((p) => p.noKey as string));
  const comAviso = new Set(problemas.filter((p) => p.nivel === 'aviso' && p.noKey).map((p) => p.noKey as string));
  const percorridos = new Set(destacados);

  // ── Arrastar passo ────────────────────────────────────────────────────────
  //
  // Os listeners ficam no `window` enquanto dura o arrasto: presos ao nó, o
  // movimento rápido do mouse sairia da caixa e o passo "grudaria" no meio do
  // caminho, largado onde o ponteiro escapou.
  useEffect(() => {
    if (!arrastando) return;

    const mover = (e: PointerEvent) => {
      const area = areaRef.current;
      if (!area) return;
      const r = area.getBoundingClientRect();
      const x = e.clientX - r.left + area.scrollLeft - arrastando.dx;
      const y = e.clientY - r.top + area.scrollTop - arrastando.dy;
      onMover(arrastando.noKey, {
        // Trava nas bordas: um passo arrastado para fora do canvas ficaria
        // invisível e sem jeito de trazer de volta.
        x: Math.max(0, Math.min(LARGURA_TELA - LARGURA_NO, Math.round(x))),
        y: Math.max(0, Math.min(ALTURA_TELA - ALTURA_NO, Math.round(y))),
      });
    };
    const soltar = () => setArrastando(null);

    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
    };
  }, [arrastando, onMover]);

  // Esc cancela a ligação em curso. Sem isso, quem começou a ligar sem querer
  // fica preso no modo até clicar em algum passo — e o clique vira uma aresta
  // que ninguém pediu.
  useEffect(() => {
    if (!ligando) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onIniciarLigacao(null);
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [ligando, onIniciarLigacao]);

  const iniciarArrasto = useCallback(
    (e: React.PointerEvent, no: NoFluxo) => {
      if (somenteLeitura) return;
      const area = areaRef.current;
      if (!area) return;
      const r = area.getBoundingClientRect();
      const p = no.posicao ?? { x: 0, y: 0 };
      setArrastando({
        noKey: no.node_key,
        dx: e.clientX - r.left + area.scrollLeft - p.x,
        dy: e.clientY - r.top + area.scrollTop - p.y,
      });
      onSelecionar(no.node_key);
    },
    [onSelecionar, somenteLeitura],
  );

  return (
    <div
      ref={areaRef}
      onClick={() => {
        onSelecionar(null);
        onIniciarLigacao(null);
      }}
      className="relative h-full w-full overflow-auto rounded-2xl bg-[radial-gradient(circle,#d8d8d2_1px,transparent_1px)] [background-size:22px_22px] ring-1 ring-gray-200 dark:bg-[radial-gradient(circle,#334155_1px,transparent_1px)] dark:ring-slate-700"
    >
      <div className="relative" style={{ width: LARGURA_TELA, height: ALTURA_TELA }}>
        <svg
          width={LARGURA_TELA}
          height={ALTURA_TELA}
          className="pointer-events-none absolute inset-0"
        >
          <defs>
            <marker id="seta" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-gray-400 dark:fill-slate-500" />
            </marker>
            <marker id="seta-ativa" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-primary" />
            </marker>
          </defs>

          {grafo.arestas.map((a: ArestaFluxo) => {
            const de = porChave.get(a.de);
            const para = porChave.get(a.para);
            if (!de || !para) return null;

            const inicio = centroSaida(de);
            const fim = centroEntrada(para);
            const meio = { x: (inicio.x + fim.x) / 2, y: (inicio.y + fim.y) / 2 };
            const ativa = percorridos.has(a.de) && percorridos.has(a.para);
            const sobre = arestaSobre === a.id;

            return (
              <g key={a.id} className="pointer-events-auto">
                {/* Linha grossa invisível: alvo de clique. A curva desenhada tem
                    2px, e acertar 2px com o mouse é frustrante. */}
                <path
                  d={caminho(inicio, fim)}
                  stroke="transparent"
                  strokeWidth={16}
                  fill="none"
                  className={somenteLeitura ? '' : 'cursor-pointer'}
                  onMouseEnter={() => setArestaSobre(a.id)}
                  onMouseLeave={() => setArestaSobre(null)}
                />
                <path
                  d={caminho(inicio, fim)}
                  strokeWidth={ativa || sobre ? 3 : 2}
                  fill="none"
                  markerEnd={ativa ? 'url(#seta-ativa)' : 'url(#seta)'}
                  className={
                    ativa
                      ? 'stroke-primary'
                      : sobre
                        ? 'stroke-red-400'
                        : 'stroke-gray-400 dark:stroke-slate-500'
                  }
                />
                {a.quando && (
                  <g transform={`translate(${meio.x}, ${meio.y})`}>
                    <rect x={-20} y={-10} width={40} height={20} rx={10} className="fill-white stroke-gray-300 dark:fill-slate-800 dark:stroke-slate-600" />
                    <text textAnchor="middle" dy={4} className="fill-gray-600 text-[11px] font-semibold dark:fill-slate-300">
                      {a.quando === 'verdadeiro' ? 'sim' : 'não'}
                    </text>
                  </g>
                )}
                {sobre && !somenteLeitura && (
                  <g
                    transform={`translate(${meio.x + 28}, ${meio.y})`}
                    className="cursor-pointer"
                    onMouseEnter={() => setArestaSobre(a.id)}
                    onMouseLeave={() => setArestaSobre(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoverAresta(a.id);
                    }}
                  >
                    <circle r={11} className="fill-red-500" />
                    <path d="M -4 -4 L 4 4 M 4 -4 L -4 4" stroke="white" strokeWidth={2} strokeLinecap="round" />
                  </g>
                )}
              </g>
            );
          })}
        </svg>

        {grafo.nos.map((no) => {
          const definicao = passo(no.tipo);
          const p = no.posicao ?? { x: 0, y: 0 };
          const ehInicial = grafo.noInicial === no.node_key;
          const alvoDeLigacao = !!ligando && ligando.de !== no.node_key;

          return (
            <div
              key={no.id}
              style={{ left: p.x, top: p.y, width: LARGURA_NO, height: ALTURA_NO }}
              onPointerDown={(e) => {
                if (alvoDeLigacao) return;
                e.stopPropagation();
                iniciarArrasto(e, no);
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (alvoDeLigacao) {
                  onLigar(no.node_key);
                  return;
                }
                onSelecionar(no.node_key);
              }}
              className={[
                'absolute select-none rounded-xl bg-white shadow-sm transition-shadow dark:bg-slate-800',
                somenteLeitura ? '' : 'cursor-grab active:cursor-grabbing',
                alvoDeLigacao ? 'ring-2 ring-primary ring-offset-2 dark:ring-offset-slate-900' : '',
                selecionado === no.node_key
                  ? 'ring-2 ring-primary'
                  : comErro.has(no.node_key)
                    ? 'ring-2 ring-red-400'
                    : comAviso.has(no.node_key)
                      ? 'ring-2 ring-secondary'
                      : 'ring-1 ring-gray-200 dark:ring-slate-700',
                percorridos.has(no.node_key) ? 'shadow-md shadow-primary/30' : '',
              ].join(' ')}
            >
              <div className={`h-1.5 rounded-t-xl ${definicao?.cor ?? 'bg-gray-300'}`} />

              <div className="flex items-start gap-1.5 px-2.5 pt-1.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold leading-tight text-gray-800 dark:text-slate-100">
                    {no.node_key}
                  </p>
                  <p className="truncate text-[11px] leading-tight text-gray-500 dark:text-slate-400">
                    {definicao?.rotulo ?? no.tipo}
                  </p>
                </div>
                {ehInicial ? (
                  <span
                    title="Passo inicial: é por aqui que o fluxo começa"
                    className="mt-0.5 shrink-0 rounded-md bg-primary/10 p-1 text-primary"
                  >
                    <Flag size={11} />
                  </span>
                ) : (
                  !somenteLeitura && (
                    <button
                      title="Tornar este o passo inicial"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDefinirInicial(no.node_key);
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      className="mt-0.5 shrink-0 rounded-md p-1 text-gray-300 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-500 group-hover:opacity-100 dark:hover:bg-slate-700 [.group:hover_&]:opacity-100"
                    >
                      <Flag size={11} />
                    </button>
                  )
                )}
              </div>

              {/* Saídas. A condição tem duas; o "fim" não tem nenhuma. */}
              {!somenteLeitura && definicao?.saida !== 'nenhuma' && (
                <div className="absolute -bottom-3 left-0 flex w-full justify-center gap-1.5">
                  {(definicao?.saida === 'condicional'
                    ? ([
                        { quando: 'verdadeiro' as const, texto: 'sim' },
                        { quando: 'falso' as const, texto: 'não' },
                      ])
                    : [{ quando: null, texto: '+' }]
                  ).map((saida) => {
                    const ativo = ligando?.de === no.node_key && ligando?.quando === saida.quando;
                    return (
                      <button
                        key={saida.texto}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          onIniciarLigacao(ativo ? null : { de: no.node_key, quando: saida.quando });
                        }}
                        title="Ligar a outro passo"
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm ring-1 transition-colors ${
                          ativo
                            ? 'bg-primary text-white ring-primary'
                            : 'bg-white text-gray-500 ring-gray-300 hover:text-primary hover:ring-primary dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600'
                        }`}
                      >
                        {saida.texto}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {grafo.nos.length === 0 && (
          <div className="pointer-events-none absolute left-1/2 top-24 w-64 -translate-x-1/2 text-center">
            <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Canvas vazio</p>
            <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
              Escolha um passo na coluna da esquerda para começar.
            </p>
          </div>
        )}
      </div>

      {ligando && (
        <div className="pointer-events-none sticky bottom-3 left-1/2 z-10 mx-auto w-fit rounded-full bg-gray-900/90 px-3.5 py-1.5 text-xs font-medium text-white shadow-lg">
          Clique no passo de destino
          {ligando.quando ? ` do "${ligando.quando === 'verdadeiro' ? 'sim' : 'não'}"` : ''} — Esc cancela
        </div>
      )}
    </div>
  );
}

/** Botão de apagar passo, usado pelo painel de propriedades. */
export function BotaoApagarPasso({ onApagar }: { onApagar: () => void }) {
  return (
    <button
      onClick={onApagar}
      className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-red-200 px-3 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-900/20"
    >
      <Trash2 size={13} />
      Apagar este passo
    </button>
  );
}
